import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { NavLink, Outlet, useParams } from 'react-router-dom';

import { EmptyState } from '@/components/common/empty-state';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { extractApiErrorMessage } from '@/lib/api';
import { getProjectByKey } from '@/lib/project-api';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';

interface ProjectLayoutContext {
  orgId: string;
  orgSlug: string;
  projectKey: string;
}

export function ProjectLayout() {
  const { orgSlug, projectKey } = useParams<{ orgSlug: string; projectKey: string }>();
  const activeOrgId = useAuthStore((s) => s.activeOrgId);

  const projectQuery = useQuery({
    queryKey: ['org', activeOrgId, 'project', projectKey],
    queryFn: () => getProjectByKey(activeOrgId!, projectKey!),
    enabled: Boolean(activeOrgId && projectKey),
  });

  if (!activeOrgId || !projectKey) {
    return (
      <div className="p-6">
        <EmptyState title="Invalid project URL" description="Pick a project from the list." />
      </div>
    );
  }

  if (projectQuery.isError) {
    return (
      <div className="mx-auto w-full max-w-6xl p-6">
        <EmptyState
          title="Could not load project"
          description={extractApiErrorMessage(projectQuery.error)}
        />
      </div>
    );
  }

  const tabs: Array<{ to: string; label: string; end: boolean }> = [
    { to: '.', label: 'Overview', end: true },
    { to: 'issues', label: 'Issues', end: false },
    { to: 'backlog', label: 'Backlog', end: false },
    { to: 'board', label: 'Board', end: false },
    { to: 'dashboard', label: 'Dashboard', end: false },
    { to: 'settings', label: 'Settings', end: false },
  ];

  return (
    <div className="flex min-h-full flex-col">
      <div className="border-b bg-background px-6 pt-6">
        <nav
          className="mb-3 flex items-center gap-1 text-xs text-muted-foreground"
          aria-label="Breadcrumb"
        >
          <span>Projects</span>
          <ChevronRight className="h-3 w-3" aria-hidden />
          <span className="font-mono">{projectKey}</span>
        </nav>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            {projectQuery.isLoading ? (
              <Skeleton className="h-7 w-48" />
            ) : (
              <h1 className="truncate text-2xl font-semibold tracking-tight">
                {projectQuery.data?.name}
              </h1>
            )}
            <p className="text-sm text-muted-foreground">
              <span className="font-mono uppercase">{projectKey}</span>
              {projectQuery.data?.archivedAt ? (
                <>
                  {' · '}
                  <Badge variant="outline">Archived</Badge>
                </>
              ) : null}
            </p>
          </div>
          {projectQuery.data?.role ? (
            <Badge variant="secondary">{projectQuery.data.role}</Badge>
          ) : null}
        </div>
        <div className="-mb-px mt-4 flex flex-wrap gap-1">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              className={({ isActive }) =>
                cn(
                  'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )
              }
            >
              {t.label}
            </NavLink>
          ))}
        </div>
      </div>

      <Outlet
        context={
          {
            orgId: activeOrgId,
            orgSlug: orgSlug!,
            projectKey: projectKey.toUpperCase(),
          } satisfies ProjectLayoutContext
        }
      />
    </div>
  );
}

export type { ProjectLayoutContext };
